import { useEffect, useState } from 'react';

/**
 * The v20 responsive table. Width decides the geometry of the computer forms
 * (column, rail, drawer, master-detail) at the pixel cuts v20 uses.
 * `touchDevice` is not a width: D-160 ruling 7 puts the navigation bar at the
 * bottom on every device that reports no hover and a coarse pointer — a phone
 * or a tablet, whatever its width — and never on a computer. CSS pixels are not
 * device pixels, which is why a pixel rule was the wrong tool for that question.
 */
export const PANEL_V2_RESPONSIVE_MEDIA = {
  navigationToggleHidden: '(max-width: 840px)',
  /** A service screen keeps its rail of icons up to here, as its wide column began past it. */
  serviceNavigationNamesHidden: '(max-width: 1200px)',
  serviceNavigationRail: '(max-width: 1200px) and (min-width: 841px)',
  navigationRail: '(max-width: 840px) and (min-width: 601px)',
  navigationDrawer: '(max-width: 600px)',
  kitchenSingleColumn: '(max-width: 700px)',
  kitchenIdleHidden: '(max-width: 1200px)',
  masterDetailSinglePanel: '(max-width: 1120px)',
  /** The TPV keeps its table list beside the open table down to the phone (staff-v20 §2.1). */
  tpvSinglePanel: '(max-width: 600px)',
  touchDevice: '(hover: none) and (pointer: coarse)',
} as const;

export const useMediaQuery = (query: string): boolean => {
  const read = () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, [query]);

  return matches;
};
